

import { useMemo } from 'react';
import { useAuth } from '@/contexts/auth.context';
import { UserPermissions } from '../enums/UserPermissions';
import { UserRole, BankRoles, ProviderRoles, InspectorRoles, isRoleFromModule } from '../enums/UserRoles';

export interface PermissionCheckOptions {
  operator?: 'OR' | 'AND';
  requireAll?: boolean;
}

export interface UsePermissionsReturn {
  hasPermission: (
    permissions: UserPermissions | UserPermissions[], 
    options?: PermissionCheckOptions
  ) => boolean;
  
  hasRole: (roles: UserRole | UserRole[], options?: PermissionCheckOptions) => boolean;
  hasAnyBankRole: () => boolean;
  hasAnyProviderRole: () => boolean;
  hasInspectorRole: () => boolean;
  
  isBankUser: () => boolean;
  isProviderUser: () => boolean;
  isInspectorUser: () => boolean;
  
  isAdmin: () => boolean;
  canManageUsers: () => boolean;
  canCreateInspections: () => boolean;
  canScheduleInspections: () => boolean;
  canReviewReports: () => boolean;
  canExportData: () => boolean;
  
  userRoles: UserRole[];
  userPermissions: UserPermissions[];
  currentModule: 'bank' | 'provider' | 'inspector' | null;
}

export function usePermissions(): UsePermissionsReturn {
  const { user } = useAuth();
  
  const userRoles = useMemo<UserRole[]>(() => {
    if (!user || !user.roles) {
      return [];
    }

    const roles = user.roles as string[];

    const validRoles = roles.filter(role =>
      Object.values({ ...BankRoles, ...ProviderRoles, ...InspectorRoles }).includes(role as UserRole)
    ) as UserRole[];

    return validRoles;
  }, [user]);

  const userPermissions = useMemo<UserPermissions[]>(() => {
    if (!user || !user.permissions) {
      return [];
    }

    const permissions = user.permissions
      .map(p => p as UserPermissions)
      .filter(p => Object.values(UserPermissions).includes(p));

    return permissions;
  }, [user]);

  const currentModule = useMemo<'bank' | 'provider' | 'inspector' | null>(() => {
    if (userRoles.some(role => isRoleFromModule(role, 'bank'))) return 'bank';
    if (userRoles.some(role => isRoleFromModule(role, 'provider'))) return 'provider';
    if (userRoles.some(role => isRoleFromModule(role, 'inspector'))) return 'inspector';
    return null;
  }, [userRoles]);

  const hasPermission = (
    permissions: UserPermissions | UserPermissions[], 
    options: PermissionCheckOptions = {}
  ): boolean => {
    if (userPermissions.length === 0) return false;
    
    const permissionsToCheck = Array.isArray(permissions) ? permissions : [permissions];
    const operator = options.requireAll ? 'AND' : (options.operator || 'OR');
    
    if (operator === 'AND') {
      return permissionsToCheck.every(permission => userPermissions.includes(permission));
    } else {
      return permissionsToCheck.some(permission => userPermissions.includes(permission));
    }
  };

  const hasRole = (
    roles: UserRole | UserRole[], 
    options: PermissionCheckOptions = {}
  ): boolean => {
    if (userRoles.length === 0) return false;
    
    const rolesToCheck = Array.isArray(roles) ? roles : [roles];
    const operator = options.requireAll ? 'AND' : (options.operator || 'OR');
    
    if (operator === 'AND') {
      return rolesToCheck.every(role => userRoles.includes(role));
    } else {
      return rolesToCheck.some(role => userRoles.includes(role));
    }
  };

  const hasAnyBankRole = (): boolean => 
    userRoles.some(role => isRoleFromModule(role, 'bank'));
    
  const hasAnyProviderRole = (): boolean => 
    userRoles.some(role => isRoleFromModule(role, 'provider'));
    
  const hasInspectorRole = (): boolean => 
    userRoles.includes(InspectorRoles.INSPECTOR);

  const isBankUser = hasAnyBankRole;
  const isProviderUser = hasAnyProviderRole;
  const isInspectorUser = hasInspectorRole;

  const isAdmin = (): boolean => 
    hasRole(BankRoles.ADMIN_BANK);

  const canManageUsers = (): boolean => 
    hasPermission([
      UserPermissions.users_create_bank,
      UserPermissions.users_create_provider,
      UserPermissions.inspectors_create_bank,
      UserPermissions.inspectors_create_provider
    ]);

  const canCreateInspections = (): boolean => 
    hasPermission(UserPermissions.inspections_create_bank);

  const canScheduleInspections = (): boolean => 
    hasPermission([
      UserPermissions.inspections_schedule_bank,
      UserPermissions.inspections_schedule_provider,
      UserPermissions.inspections_schedule_inspector
    ]);

  const canReviewReports = (): boolean => 
    hasPermission([
      UserPermissions.reports_review_bank,
      UserPermissions.reports_review_provider
    ]);

  const canExportData = (): boolean => 
    hasPermission(UserPermissions.data_export_bank);

  return {
    hasPermission,
    hasRole,
    hasAnyBankRole,
    hasAnyProviderRole,
    hasInspectorRole,
    isBankUser,
    isProviderUser,
    isInspectorUser,
    isAdmin,
    canManageUsers,
    canCreateInspections,
    canScheduleInspections,
    canReviewReports,
    canExportData,
    userRoles,
    userPermissions,
    currentModule
  };
}